import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';

const FADE = 24; // px of edge fade where more content is hidden
const BAR = 10;  // bottom strip kept unmasked so the scrollbar never fades

/**
 * ScrollX - horizontal scroll container for wide tables/grids.
 * Mouse users get a visible scrollbar, an edge fade that signals hidden
 * columns, and keyboard focus (arrow keys scroll once focused).
 * The wheel is never hijacked, so touchpad page scrolling stays native.
 */
export default function ScrollX({ children, sx, ...props }) {

  const ref = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [update]);

  const scrollable = edges.left || edges.right;
  const l = edges.left ? FADE : 0;
  const r = edges.right ? FADE : 0;
  const mask = scrollable ? {
    maskImage: `linear-gradient(to right, transparent 0, #000 ${l}px, #000 calc(100% - ${r}px), transparent 100%), linear-gradient(#000, #000)`,
    maskSize: `100% 100%, 100% ${BAR}px`,
    maskPosition: '0 0, 0 100%',
    maskRepeat: 'no-repeat',
    WebkitMaskImage: `linear-gradient(to right, transparent 0, #000 ${l}px, #000 calc(100% - ${r}px), transparent 100%), linear-gradient(#000, #000)`,
    WebkitMaskSize: `100% 100%, 100% ${BAR}px`,
    WebkitMaskPosition: '0 0, 0 100%',
    WebkitMaskRepeat: 'no-repeat',
  } : {};

  return (
    <Box
      ref={ref}
      onScroll={update}
      tabIndex={scrollable ? 0 : undefined}
      role={scrollable && props['aria-label'] ? 'region' : undefined}
      {...props}
      sx={[
        {
          overflowX: 'auto',
          ...mask,
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}
